/**
 * Ikony aplikacji.
 *
 * IC-1: jeden jezyk - styl liniowy, kreska 2px w skali 24, `currentColor`, bez wypelnien.
 * IC-2: rozmiary ze skali. Trzymamy 18px dla ikon w kontrolkach i 20px w nawigacji,
 *       konsekwentnie w calej aplikacji - gotowce uzywaja 18px i to jest zrodlo prawdy
 *       dla przyciskow.
 * IC-4: ikony pochodza z kanonicznego snippets/icons.html. Ustawienia to ZEBATKA,
 *       nigdy slonce (slonce znaczy tryb jasny motywu).
 * C-1:  jedna ikona = jedno znaczenie w widoku. Zaden ksztalt nie powtarza sie tu
 *       w dwoch rolach.
 *
 * Mikrofon, odtwarzanie i wgrywanie rysujemy sami w tym samym stylu - `icons.html`
 * ich nie ma, bo to pojecia specyficzne dla ConvoLens.
 */

interface Props {
  /** Rozmiar w px. Domyslnie 18 - tyle maja ikony w kontrolkach w gotowcach. */
  size?: number;
  className?: string;
}

function Svg({ size = 18, className, children }: Props & { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Nawigacja: koszty. Trzy pionowe kreski z icons.html ("Statystyki - koszty / analityka"), 1:1. */
export function IconKoszty(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <line x1="6" y1="20" x2="6" y2="16" />
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
    </Svg>
  );
}

/**
 * Nawigacja: Developer. Terminal: znak zachety i kreska wiersza polecen. icons.html nie ma ikony kodu ani terminala,
 * wiec rysujemy ja w tym samym stylu (IC-1: liniowa, kreska 2px w skali 24, currentColor), jak mikrofon i wgrywanie.
 */
export function IconDeveloper(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </Svg>
  );
}

/** Pole wyszukiwania (F-10): lupa wewnatrz pola po lewej. 1:1 z icons.html ("Szukaj", wyszukiwanie F-10). */
export function IconSzukaj(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </Svg>
  );
}

/** Kopiowanie tresci do schowka (WY-5: akcje kopiuj/pobierz). 1:1 z icons.html ("Kopiuj", copy). */
export function IconKopiuj(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </Svg>
  );
}

/** Paginacja (PG-1): poprzednia strona. Sciezka 1:1 z paginacji w data-table.html. */
export function IconChevronLewo(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M15 18l-6-6 6-6" />
    </Svg>
  );
}

/** Paginacja (PG-1): nastepna strona. Sciezka 1:1 z paginacji w data-table.html. */
export function IconChevronPrawo(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M9 18l6-6-6-6" />
    </Svg>
  );
}

/** Nawigacja: lista analiz. Dokument z liniami tekstu (icons.html, "Dokumenty"). */
export function IconAnalizy(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </Svg>
  );
}

/** Nawigacja: tryb mikrofonu. Rysowany wg IC-1 - icons.html nie ma mikrofonu. */
export function IconMikrofon(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </Svg>
  );
}

/** Nawigacja: ustawienia. IC-4 wymienia z nazwy: zebatka, NIGDY slonce. */
export function IconUstawienia(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

/** Motyw jasny (B-9). */
export function IconSlonce(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
    </Svg>
  );
}

/** Motyw ciemny (B-9). */
export function IconKsiezyc(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </Svg>
  );
}

export function IconWyloguj(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Svg>
  );
}

export function IconStrzalkaDol(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="m6 9 6 6 6-6" />
    </Svg>
  );
}

export function IconTelefon(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
    </Svg>
  );
}

export function IconKoperta(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 6 10 7L22 6" />
    </Svg>
  );
}

/** Zamkniecie: toast (TO-6), baner (BN-2), usuniecie pliku z listy (UP-3). 1:1 z icons.html. */
export function IconKrzyzyk(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Svg>
  );
}

/** Potwierdzenie wyboru: ptaszek przy wybranej opcji selecta (F-6), etap zrobiony (PP-1). */
export function IconPtaszek(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M20 6 9 17l-4-4" />
    </Svg>
  );
}

/** Eksport pliku (TB-9): "Pobierz raport". Strzalka w dol nad tacka, 1:1 z icons.html ("Pobierz"). */
export function IconPobierz(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </Svg>
  );
}

/** Ponowienie (icons.html, "Odswiez"): "Ocen ponownie". Dwie strzalki w kole. */
export function IconOdswiez(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </Svg>
  );
}

/** Status ostrzezenia (BN-2, S-9). Trojkat z wykrzyknikiem, 1:1 z icons.html. */
export function IconOstrzezenie(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Svg>
  );
}

/** Status informacji (BN-2). Kolko z "i", 1:1 z icons.html. */
export function IconInformacja(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </Svg>
  );
}

/** Status bledu (BN-2, PP-1). Kolko z krzyzykiem - inny ksztalt niz IconKrzyzyk (C-1). */
export function IconBlad(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </Svg>
  );
}

/** Status sukcesu (icons.html, "Sukces"): kolko z ptaszkiem. Znak sekcji "Co wyszlo najlepiej". */
export function IconSukces(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}

/**
 * Rzecz do poprawy: kolko z wykrzyknikiem, w stylu IC-1.
 *
 * icons.html ma w statusach tylko blad (kolko z krzyzykiem, `IconBlad`) i ostrzezenie (trojkat), a "do poprawy" nie jest
 * ani bledem, ani ostrzezeniem aplikacji: to uwaga o wypowiedzi. Trojkat jest w tej samej karcie znakiem ostrzezenia
 * o zadaniu oceny (C-1), a krzyzyk czytaloby sie jak "zle", wiec rysujemy wlasny ksztalt, jak przy mikrofonie czy wgrywaniu.
 */
export function IconDoPoprawy(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </Svg>
  );
}

/**
 * Odtwarzanie nagrania (WY-3).
 *
 * ROZJAZD: special-widgets.html rysuje play i pauze jako ksztalty WYPELNIONE
 * (fill="currentColor"). IC-1 wymaga jednego jezyka ikon - obrys ~2px - a jedna
 * wypelniona ikona w calej aplikacji wyglada jak cialo obce. Rysujemy obrysem.
 */
export function IconPlay(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M7 4.5v15l12-7.5z" />
    </Svg>
  );
}

/** Pauza nagrania (WY-3). Obrys wg IC-1, patrz nota przy IconPlay. */
export function IconPauza(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M9 5v14" />
      <path d="M15 5v14" />
    </Svg>
  );
}

/** Strefa wgrywania (UP-1). Ksztalt 1:1 z upload.html. */
export function IconUpload(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </Svg>
  );
}

/** Nawigacja: Recenzje. Dymek z dwiema liniami tekstu (w stylu IC-1; icons.html nie ma ikony recenzji). */
export function IconRecenzje(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M13 8H7" />
      <path d="M17 12H7" />
    </Svg>
  );
}

/** Link do udostepnienia ("Wyslij do recenzji"). Dwa ogniwa lancucha, w stylu IC-1. */
export function IconLink(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Svg>
  );
}

/**
 * "Dodaj uwage" przy wypowiedzi (widok recenzenta). Dymek z plusem, w stylu IC-1; icons.html nie ma takiej ikony, a
 * zatwierdzona makieta rysuje wlasnie ten ksztalt.
 */
export function IconDodajUwage(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6" />
      <path d="M9 10h6" />
    </Svg>
  );
}

/** Akcja "Edytuj". 1:1 z icons.html (grupa "Akcje", IC-4). */
export function IconEdytuj(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </Svg>
  );
}

/** Akcja "Usun" (kosz). 1:1 z icons.html (grupa "Akcje", IC-4). */
export function IconKosz(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </Svg>
  );
}

/**
 * Nawigacja konta `user`: Cwiczenia. Tarcza z trzema kregami (cel), w stylu IC-1; icons.html nie ma ikony celu, a
 * zatwierdzona makieta widoku uzytkownika rysuje wlasnie ten ksztalt. Inny niz kazda ikona menu administratora (C-1).
 */
export function IconCwiczenia(p: Props): React.JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </Svg>
  );
}
